/** Shared batch publish suspension helpers (Trading Dash + admin page). */

export const BATCH_SUSPENSION_SPORT_BUCKETS = [
  { value: 'soccer', label: 'Soccer' },
  { value: 'basketball', label: 'NCAAB / CBB' },
  { value: 'nba', label: 'NBA' },
  { value: 'wnba', label: 'WNBA' },
  { value: 'football', label: 'NFL' },
  { value: 'ncaaf', label: 'NCAAF / CFB' },
  { value: 'baseball', label: 'MLB' },
  { value: 'esports', label: 'Esports' },
  { value: 'hockey', label: 'NHL' },
];

/**
 * Writer targets for Model Markets hub (api_only proxies rules/ensure/ingest here).
 * ``sport`` / ``league`` must match ``routing_key_for`` for the bucket.
 */
export const MODEL_MARKET_WRITER_TARGETS = [
  {
    bucket: 'wnba',
    sport: 'basketball',
    league: 'wnba',
    label: 'WNBA / All-Star',
    playerSyncSport: 'wnba',
  },
  {
    bucket: 'football',
    sport: 'football',
    league: 'nfl',
    label: 'NFL',
    playerSyncSport: 'nfl',
  },
  {
    bucket: 'ncaaf',
    sport: 'football',
    league: 'ncaaf',
    label: 'NCAAF / CFB',
    playerSyncSport: 'cfb',
  },
  {
    bucket: 'nba',
    sport: 'basketball',
    league: 'nba',
    label: 'NBA',
    playerSyncSport: 'nba',
  },
  {
    bucket: 'basketball',
    sport: 'basketball',
    league: 'ncaab',
    label: 'NCAAB / CBB',
    playerSyncSport: 'cbb',
  },
  {
    bucket: 'soccer',
    sport: 'soccer',
    league: 'epl',
    label: 'Soccer',
    playerSyncSport: 'epl',
  },
  {
    bucket: 'baseball',
    sport: 'baseball',
    league: 'mlb',
    label: 'MLB',
    playerSyncSport: 'mlb',
  },
  {
    bucket: 'esports',
    sport: 'esports',
    league: '',
    label: 'Esports',
    playerSyncSport: '',
  },
  {
    // NHL writer (config-nhl.yaml). Bucket is the coarse sport, so sportBucketForFixture needs
    // no branch: an unrecognised sport already passes through as its own bucket.
    bucket: 'hockey',
    sport: 'hockey',
    league: 'nhl',
    label: 'NHL',
    playerSyncSport: 'nhl',
  },
];

export function modelMarketWriterTarget(bucket) {
  const key = String(bucket || '').trim().toLowerCase();
  return (
    MODEL_MARKET_WRITER_TARGETS.find((t) => t.bucket === key)
    || MODEL_MARKET_WRITER_TARGETS[0]
  );
}

export const BATCH_SUSPENSION_SCOPE_OPTIONS = [
  { value: 'fixture', label: 'Full game', group: 'game' },
  { value: 'fixture_team', label: 'Team in game', group: 'game' },
  { value: 'fixture_player', label: 'Player in game', group: 'game' },
  { value: 'league', label: 'One league', group: 'league' },
  { value: 'league_wager', label: 'League + market type', group: 'league' },
  { value: 'all', label: 'Entire sport bucket', group: 'league' },
];

export const FIXTURE_BATCH_SCOPES = new Set(['fixture', 'fixture_team', 'fixture_player']);

export function isFixtureBatchScope(scope) {
  return FIXTURE_BATCH_SCOPES.has(String(scope || '').trim());
}

/** Map fixture sport/league to writer routing bucket (mirrors backend ``routing_key_for``). */
export function sportBucketForFixture(fixture) {
  const sport = String(fixture?.sport || '').trim().toLowerCase();
  const league = String(fixture?.league || '').trim().toLowerCase();
  const tokens = [sport, ...sport.split(/[:|]/).map((t) => t.trim()).filter(Boolean)];
  let bucket = sport;
  for (const tok of tokens) {
    if (['soccer', 'clubsoccer', 'intlsoccer'].includes(tok)) {
      bucket = 'soccer';
      break;
    }
    if (['basketball', 'cbb', 'ncaab', 'wnba', 'nba'].includes(tok)) {
      bucket = 'basketball';
      break;
    }
    if (['ncaaf', 'cfb'].includes(tok)) {
      bucket = 'ncaaf';
      break;
    }
    if (['football', 'nfl'].includes(tok)) {
      bucket = 'football';
      break;
    }
    if (['baseball', 'mlb'].includes(tok)) {
      bucket = 'baseball';
      break;
    }
    if (
      [
        'esports',
        'cs2',
        'cs',
        'cs_go',
        'valorant',
        'dota',
        'dota_2',
        'lol',
        'league_of_legends',
        'call_of_duty',
      ].includes(tok)
    ) {
      bucket = 'esports';
      break;
    }
  }
  if (bucket === 'basketball' && (sport.includes('wnba') || league.includes('wnba'))) {
    return 'wnba';
  }
  // NBA runs its own writer; NCAAB/CBB keep the coarse ``basketball`` bucket. Must follow the
  // WNBA check -- 'nba' is a substring of 'wnba' -- and match whole tokens so 'ncaab' misses.
  if (
    bucket === 'basketball'
    && (league === 'nba' || sport === 'nba' || tokens.includes('nba'))
  ) {
    return 'nba';
  }
  if (
    league.includes('ncaaf')
    || league === 'cfb'
    || league.includes('cfb')
    || sport.includes('ncaaf')
    || sport === 'cfb'
  ) {
    return 'ncaaf';
  }
  if (league.includes('nfl') || sport.includes('nfl') || sport.includes('football')) {
    return 'football';
  }
  return bucket || 'soccer';
}

function suspensionFieldEqual(a, b) {
  const left = a == null ? '' : String(a).trim();
  const right = b == null ? '' : String(b).trim();
  return left === right;
}

export function findActiveBatchSuspension(
  suspensions,
  {
    sportBucket,
    scope,
    fixtureId,
    playerId,
    teamSide,
    league,
    wagerType,
  }
) {
  const rows = Array.isArray(suspensions) ? suspensions : [];
  return (
    rows.find(
      (row) =>
        row
        && !row.valid_to
        && suspensionFieldEqual(row.sport_bucket, sportBucket)
        && suspensionFieldEqual(row.scope, scope)
        && suspensionFieldEqual(row.fixture_id, fixtureId)
        && suspensionFieldEqual(row.player_id, playerId)
        && suspensionFieldEqual(row.team_side, teamSide)
        && suspensionFieldEqual(row.league, league)
        && suspensionFieldEqual(row.wager_type, wagerType)
    ) || null
  );
}

export function batchSuspendButtonLabel(scopeLabel, active) {
  return active
    ? `Clear batch suspend ${scopeLabel}`
    : `Batch suspend ${scopeLabel}`;
}

export function batchSuspensionScopeLabel(scope) {
  const hit = BATCH_SUSPENSION_SCOPE_OPTIONS.find((o) => o.value === scope);
  return hit?.label || scope || '—';
}

/** Human-readable target for list rows (admin + debugging). */
export function formatBatchSuspensionTarget(row) {
  if (!row) return '';
  const scope = String(row.scope || '').trim();
  if (scope === 'fixture') return `game ${row.fixture_id || '—'}`;
  if (scope === 'fixture_player') {
    return `game ${row.fixture_id || '—'} · player ${row.player_id || '—'}`;
  }
  if (scope === 'fixture_team') {
    // team_side is 'home'/'away' for a game, or a club selector on a futures shell — reading
    // "· Buffalo Bills team" as a side would be misleading, so label the two cases apart.
    const ts = String(row.team_side || '').trim();
    const side = ts.toLowerCase();
    const target = side === 'home' || side === 'away' ? `${side} team` : `club ${ts || '—'}`;
    return `game ${row.fixture_id || '—'} · ${target}`;
  }
  if (scope === 'league') return row.league || '—';
  if (scope === 'league_wager') {
    return [row.league, row.wager_type].filter(Boolean).join(' · ') || '—';
  }
  if (scope === 'all') return 'all markets in bucket';
  return '';
}

export function normalizeBatchSuspensionFixture(row) {
  if (!row) return null;
  return {
    game_id: row.fixture_id || row.game_id,
    home_team: row.home_team,
    away_team: row.away_team,
    sport: row.sport,
    league: row.league,
    start_time_utc: row.start_time_utc,
    sleeper_game_id: row.sleeper_game_id || null,
  };
}

export function batchSuspensionFixtureLabel(fixture) {
  if (!fixture?.game_id) return '';
  const home = fixture.home_team || 'Home';
  const away = fixture.away_team || 'Away';
  return `${home} vs ${away}`;
}

export function playerTeamSideForQuote(quote) {
  const side = String(quote?.player_team_side || '').trim().toLowerCase();
  if (side === 'home' || side === 'away') return side;
  return null;
}

const BATCH_SCOPE_RANK = {
  fixture_player: 5,
  fixture_team: 4,
  fixture: 3,
  league_wager: 2,
  league: 1,
  all: 0,
};

function batchRowMatchesQuote(row, quote, fixture, sportBucket) {
  if (!row || row.valid_to) return false;
  const scope = String(row.scope || '').trim();
  const fixtureId = quote.fixture_id || fixture?.game_id;
  if (scope === 'fixture') {
    return suspensionFieldEqual(row.fixture_id, fixtureId);
  }
  if (scope === 'fixture_player') {
    return suspensionFieldEqual(row.fixture_id, fixtureId)
      && suspensionFieldEqual(row.player_id, quote.player_id);
  }
  if (scope === 'fixture_team') {
    return suspensionFieldEqual(row.fixture_id, fixtureId)
      && suspensionFieldEqual(row.team_side, playerTeamSideForQuote(quote));
  }
  if (scope === 'league') {
    const league = quote.league || fixture?.league;
    return suspensionFieldEqual(row.sport_bucket, sportBucket)
      && suspensionFieldEqual(row.league, league);
  }
  if (scope === 'league_wager') {
    const league = quote.league || fixture?.league;
    const mid = String(quote.market_id || '').trim().toLowerCase();
    const wt = String(row.wager_type || '').trim().toLowerCase();
    return suspensionFieldEqual(row.sport_bucket, sportBucket)
      && suspensionFieldEqual(row.league, league)
      && Boolean(mid) && mid === wt;
  }
  if (scope === 'all') {
    return suspensionFieldEqual(row.sport_bucket, sportBucket);
  }
  return false;
}

/** First matching active batch rule for a quote (most specific scope wins). */
export function batchPublishSuspensionForQuote(quote, suspensions, fixture) {
  if (!quote || !fixture) return null;
  const sportBucket = sportBucketForFixture(fixture);
  const rows = Array.isArray(suspensions) ? suspensions : [];
  let best = null;
  let bestRank = -1;
  for (const row of rows) {
    if (!batchRowMatchesQuote(row, quote, fixture, sportBucket)) continue;
    const rank = BATCH_SCOPE_RANK[String(row.scope || '').trim()] ?? -1;
    if (rank > bestRank) {
      bestRank = rank;
      best = row;
    }
  }
  if (!best) return null;
  return {
    id: best.id,
    scope: best.scope,
    scopeLabel: batchSuspensionScopeLabel(best.scope),
    reason: best.reason || 'Fixture suspension',
  };
}

export function batchSuspensionFlagLabel(scope) {
  const sk = String(scope || '').trim();
  if (sk === 'fixture') return 'batch · game';
  if (sk === 'fixture_team') return 'batch · team';
  if (sk === 'fixture_player') return 'batch · player';
  if (sk === 'league') return 'batch · league';
  if (sk === 'league_wager') return 'batch · market';
  if (sk === 'all') return 'batch · bucket';
  return 'batch suspended';
}

export function batchSuspensionConfirmCopy({
  clearing,
  scope,
  fixtureLabel,
  playerName,
  teamName,
  teamSide,
}) {
  const game = fixtureLabel || 'this game';
  const payloadNote = 'Payload-only — KF / market_quotes unchanged. Writers republish after confirm.';

  if (clearing) {
    if (scope === 'fixture_player') {
      const who = playerName || 'this player';
      return {
        title: 'Clear batch suspend player',
        body: `Remove batch publish suspension for ${who} in ${game}? ${payloadNote}`,
        confirmLabel: 'Clear suspension',
      };
    }
    if (scope === 'fixture_team') {
      const side = teamSide === 'home' ? 'home' : 'away';
      const who = teamName || `${side} team`;
      return {
        title: 'Clear batch suspend team',
        body: `Remove batch publish suspension for ${who} in ${game}? ${payloadNote}`,
        confirmLabel: 'Clear suspension',
      };
    }
    return {
      title: 'Clear batch suspend game',
      body: `Remove batch publish suspension for all markets in ${game}? ${payloadNote}`,
      confirmLabel: 'Clear suspension',
    };
  }

  if (scope === 'fixture_player') {
    const who = playerName || 'this player';
    return {
      title: 'Batch suspend player',
      body: `Suspend all markets for ${who} in ${game} in published payloads? ${payloadNote}`,
      confirmLabel: 'Batch suspend',
      danger: true,
    };
  }
  if (scope === 'fixture_team') {
    const side = teamSide === 'home' ? 'home' : 'away';
    const who = teamName || `${side} team`;
    return {
      title: 'Batch suspend team',
      body: `Suspend all markets for ${who} in ${game} in published payloads? ${payloadNote}`,
      confirmLabel: 'Batch suspend',
      danger: true,
    };
  }
  return {
    title: 'Batch suspend game',
    body: `Suspend all markets in ${game} in published payloads? ${payloadNote}`,
    confirmLabel: 'Batch suspend',
    danger: true,
  };
}
